// JobCard.tsx
import React, {useState} from 'react';
import {IonIcon, useIonAlert} from '@ionic/react';
import {locationOutline, lockClosedOutline, cashOutline, shareSocialOutline, trash, pencil, eye} from 'ionicons/icons';
import moment from "moment-timezone";
import useStore from "../zustand/useStore";
import {actionToApplyForJobPortalJobPost, actionToDeleteJobPortalJobPost} from "../apiHelpers/CommonAction";
import AddJobFormModal from "./AddJobFormModal";
import {Capacitor} from "@capacitor/core";
import { Share } from '@capacitor/share';
import JobMembersToApplyComponents from "./JobMembersToApplyComponents";

export default function JobCard({ job,handleJobItemClick,myJobPostsData,selectedId }) {
    const {userAuthDetail,userJobProfileData} = useStore();
    const jobProfileData = userJobProfileData?.jobProfileData;
    const {userInfo} = userAuthDetail;
    const [presentAlert] = useIonAlert();
    const [isLoading,setIsLoading] = useState(false);
    const [dataForEditing,setDataForEditing] = useState(null);
    const [addJobModalSheet, setAddJobModalSheet] = useState(false);

    const openMailClient = async (jobData) => {
        let jobApplyJson = [];
        if (jobData?.applied_member_id_array) {
            jobApplyJson = JSON.parse(jobData.applied_member_id_array);
        }

        if (!jobApplyJson?.includes(userInfo?.id)) {
            jobApplyJson.push(userInfo?.id);
            const template = await actionToApplyForJobPortalJobPost(jobData?.id, JSON.stringify(jobApplyJson));
            const subject = encodeURIComponent(`Application for Job – ${jobData?.title}`);

            let body = "Hello,\n\nI am interested in applying for this job position.\n\nThanks.";

            if (template) {
                let filledTemplate = template
                    .replace(/{JOB_TITLE}/g, jobData?.title || "-")
                    .replace(/{CV_URL}/g, jobProfileData?.cv_url || "")
                    .replace(/{NAME}/g, jobProfileData?.name || "")
                    .replace(/{MOBILE}/g, userInfo?.phone_number || "")
                    .replace(/{EMAIL}/g, jobProfileData?.email || "");

                body = encodeURIComponent(filledTemplate);
            }

            window.location.href = `mailto:${jobData?.apply_email}?subject=${subject}&body=${body}`;
        }
    };


    const applyForJob = (jobData)=>{
        if(jobProfileData?.id){
            presentAlert({
                header: 'Are you sure?',
                cssClass: 'custom_site_alert_toast',
                message: 'You want to apply for this job.',
                buttons: [
                    {text: 'Cancel', role: 'cancel'},
                    {text: 'Apply', role: 'confirm', handler: () =>openMailClient(jobData)},
                ],
            });
        }else{
            presentAlert({
                header: 'Sorry!',
                cssClass: 'custom_site_alert_toast',
                message: 'To apply for job please create your job profile.',
                buttons: [
                    {text: 'Okay', role: 'cancel'}
                ],
            });
        }
    }

    const callFunctionToDeleteJob = (id)=>{
        presentAlert({
            header: 'Are you sure?',
            cssClass: 'custom_site_alert_toast',
            message: 'You want to delete this job.',
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {text: 'Delete', role: 'confirm', handler: async () =>{
                    await actionToDeleteJobPortalJobPost(id)
                }},
            ],
        });
    }

    const handleShareUrl = async (jobData) => {
        try {
            const url = `https://caworld.org/api-ca-world/common/shareAppContentData/job/${jobData?.id}`;
            const title = 'CA World Job';
            const text = 'Check out this job detail!';

            // Prefer native Share plugin when running in a Capacitor app.
            // Don't gate on canShare() for text/URL — it's mainly helpful for FILE sharing.
            const isNative = Capacitor.getPlatform() !== 'web';
            const hasPlugin = Capacitor.isPluginAvailable?.('Share');

            if (isNative && hasPlugin) {
                await Share.share({
                    title,
                    text,
                    url,                  // supports http/https and file://
                    dialogTitle: 'Share this news', // Android only
                });
                return;
            }

            // Web Share API (must be triggered from a direct user gesture)
            if (navigator?.share) {
                await navigator.share({ title, text, url });
                return;
            }

            // Fallbacks
            try {
                await navigator?.clipboard?.writeText?.(url);
                presentAlert({
                    header: 'Error Sharing!',
                    message: 'Link copied to clipboard!',
                    buttons: [{
                        text: 'Ok',
                    }],
                });
            } catch {
                window.prompt('Sharing is not supported. Copy this link:', url);
            }
        } catch (err) {
            console.error('Sharing failed:', err);
            presentAlert({
                header: 'Error Sharing!',
                message: 'Failed to share!',
                buttons: [{
                    text: 'Ok',
                }],
            });
        }
    };

    const callFunctionToSetEditData = (data)=>{
        setDataForEditing(data);
        setAddJobModalSheet(true);
    }

    return (
        <div key={job.id} className={`job-card ${selectedId === job?.id ? 'active' : ''}`}>
            <div className={"d-flex ion-align-items-center job-card-header"}>
                <div className="company-icon" onClick={() => handleJobItemClick(job)}>
                    {(job?.image_url) ?
                        <img alt={"company"} src={job?.image_url}/>
                        :
                        <div className="name_initial">{job?.title?.substring(0,1).toUpperCase()}</div>
                    }
                </div>
                <div className={`approved_tag_section_tag ${job?.is_approved ? 'approved' : 'not_approved'}`}>
                    {job?.is_approved ? 'Approved' : 'Pending'}
                </div>
            </div>
            <hr/>
            <div className="job-info">
                <h3 onClick={() => handleJobItemClick(job)}>{job.title}</h3>
                <div className="company-rating">
                    {job.company}
                </div>
                <div className="job-meta" onClick={() => handleJobItemClick(job)}>
                    <span><IonIcon icon={locationOutline}/> {job.location} &nbsp;|&nbsp;</span>
                    <span><IonIcon icon={lockClosedOutline}/> {job.experience} Years</span>
                    <span>{job.salary_min && job.salary_max && (
                        <>
                            &nbsp;|&nbsp; <IonIcon icon={cashOutline}/>
                            {(job.salary_min / 100000)} - {(job.salary_max / 100000)} Lakh P.A
                        </>
                    )}</span>
                </div>
                <div className="tags-row" onClick={() => handleJobItemClick(job)}>
                    <span className="job-tag">Department</span>
                    <span className="job-tag">{job.department}</span>
                    <div className="posted">
                        {
                            (() => {
                                const daysLeft = moment(job.valid_date).startOf('day').diff(moment().startOf('day'), 'days');
                                return daysLeft < 0
                                    ? 'Expired'
                                    : `${daysLeft} Day${daysLeft !== 1 ? 's' : ''} Left`;
                            })()
                        }
                    </div>
                </div>


                <div className={"in_card_action_buttons_container"}>
                    {(selectedId !== job?.id) ?
                        <div onClick={()=>handleJobItemClick(job)} className="in_card_action_button_edit_del open">
                            <IonIcon icon={eye}/>
                        </div>:''
                    }
                    {(userInfo?.id === job?.created_by) ?
                        <>
                            <div onClick={()=>callFunctionToSetEditData(job)} className="in_card_action_button_edit_del edit">
                                <IonIcon icon={pencil}/>
                            </div>
                            <div onClick={()=>callFunctionToDeleteJob(job?.id)} className="in_card_action_button_edit_del delete">
                                <IonIcon icon={trash}/>
                            </div>
                        </>
                        :
                        <>
                        {(userInfo?.id !== job?.created_by) &&
                            (<>
                                {(job?.applied_member_id_array && JSON.parse(job?.applied_member_id_array).includes(userInfo?.id)) ?
                                    <div className="in_card_action_button_edit_del applied">
                                        Applied
                                    </div>
                                    :
                                    <div onClick={() => applyForJob(job)} className="in_card_action_button_edit_del apply">
                                        Apply
                                    </div>
                                }
                            </>)
                        }
                        </>
                    }
                    <div className="in_card_action_button_edit_del share">
                        <IonIcon icon={shareSocialOutline} onClick={()=>handleShareUrl(job)}></IonIcon>
                    </div>
                </div>
            </div>
            {/*/////// JOB POSTING //////////////*/}
            {(addJobModalSheet) ?
               <AddJobFormModal isOpen={addJobModalSheet} myJobPostsData={myJobPostsData} fromJobList={true} onClose={() => setAddJobModalSheet(false)} setIsLoading={setIsLoading} dataForEditing={dataForEditing}/> :''
            }
            {/*/////// JOB POSTING //////////////*/}

        </div>
    );
}
